import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIdentity } from '../../lib/identity'
import { markInviteDelivered, readInviteStash, stashInviteToken } from '../../lib/inviteToken'
import { signupWizardInFlight } from '../signup/SignupWizard'

/**
 * InviteWatcher — the one piece of app-root wiring the invite flow needs.
 *
 * `functions/src/org/invites.ts` mails `…/signup?invite=<token>`. That URL has
 * to serve two very different people:
 *
 *   • a BRAND-NEW human — the wizard is exactly right for them, so this stays
 *     out of the way and only STASHES the token (the Firebase account is
 *     created mid-wizard, so a React state would not survive the flow). When
 *     the wizard finishes and they land signed-in with nothing in the URL, the
 *     stash is what carries the invite to `/invite`.
 *
 *   • an EXISTING signed-in user — `/signup` bounces them to the catalog, so
 *     without this their invite silently evaporates. They go to `/invite`.
 *
 * ── THE REDIRECT IS A ONE-SHOT, AND THAT IS THE WHOLE POINT ───────────────
 * The first cut redirected on EVERY navigation for as long as the 30-minute
 * stash lived. So "Not now", "Browse the catalog" and "Sign in as the invited
 * person" all navigated away and were immediately yanked back: a focus trap
 * with no exit, rebuilt in the one flow docs/ORG-FRONTEND-PLAN.md's
 * no-dead-ends table names first.
 *
 * Now the watcher DELIVERS a token once — marking it delivered in the stash
 * *before* navigating, so the mark survives the reload/remount a redirect
 * causes — and never again. What replaces the force:
 *
 *   • the invite stays RESUMABLE from a normal place. `usePendingInvite()`
 *     backs a row in the desktop account menu and one on the mobile Profile
 *     screen, both routing to `/invite` when the user chooses to.
 *   • re-opening the emailed link (a fresh `?invite=` in the URL) re-arms it,
 *     because that is an explicit act.
 *
 * ── WHY IT IS INERT WITH THE FLAGS OFF ────────────────────────────────────
 * It renders nothing, and it does nothing at all unless a token is present in
 * the URL or the stash. With `ORGS_LIVE` off no invite exists, so no token
 * exists, so this never fires and the account menu and every route behave
 * exactly as they do today.
 *
 * ── AND WHY IT WILL NOT YANK SOMEBODY OUT OF THE WIZARD ───────────────────
 * The wizard creates the account at the credentials step — the user flips
 * signed-in with phone, address and documents still to go. `signupWizardInFlight()`
 * is the same guard both shells' auth gates use for exactly this, so a
 * mid-wizard invitee is never redirected out of their own signup.
 */
export function InviteWatcher() {
  const location = useLocation()
  const navigate = useNavigate()
  const identity = useIdentity()
  /** Belt to the stash's braces: sessionStorage can be unavailable (Safari
   *  private mode), and without a second latch the redirect would loop there. */
  const deliveredHere = useRef<string | null>(null)

  useEffect(() => {
    if (location.pathname === '/invite') return
    const fromUrl = new URLSearchParams(location.search).get('invite')
    if (fromUrl) stashInviteToken(fromUrl)

    const stash = readInviteStash()
    const token = fromUrl || stash?.token
    if (!token) return
    // A signed-out visitor belongs in the wizard; they are the person the
    // emailed link's landing page was designed for.
    if (!identity.signedIn) return
    if (signupWizardInFlight()) return
    // ALREADY DELIVERED ⇒ LEAVE THEM ALONE. Only a fresh `?invite=` re-arms.
    if (!fromUrl && (stash?.delivered || deliveredHere.current === token)) return

    deliveredHere.current = token
    markInviteDelivered()
    navigate(`/invite?token=${encodeURIComponent(token)}`, { replace: true })
  }, [location.pathname, location.search, identity.signedIn, navigate])

  return null
}
